import { useEffect, useRef } from 'react';

interface GeofenceOverlayProps {
  map: google.maps.Map;
  center: google.maps.LatLngLiteral;
  radius: number; // in meters
  fillColor?: string;
  strokeColor?: string;
  fillOpacity?: number;
  strokeOpacity?: number;
  strokeWeight?: number;
  editable?: boolean;
  draggable?: boolean;
  onClick?: () => void;
  onCenterChanged?: (center: google.maps.LatLngLiteral) => void;
  onRadiusChanged?: (radius: number) => void;
  type?: 'SAFE_ZONE' | 'RESTRICTED_ZONE' | 'ALERT_ZONE';
}

const getGeofenceColors = (type?: string) => {
  switch (type) {
    case 'SAFE_ZONE':
      return {
        fillColor: '#10B981', // green
        strokeColor: '#059669',
      };
    case 'RESTRICTED_ZONE':
      return {
        fillColor: '#EF4444', // red
        strokeColor: '#DC2626',
      };
    case 'ALERT_ZONE':
      return {
        fillColor: '#F59E0B', // amber
        strokeColor: '#D97706',
      };
    default:
      return {
        fillColor: '#3B82F6', // blue
        strokeColor: '#1D4ED8',
      };
  }
};

const GeofenceOverlay: React.FC<GeofenceOverlayProps> = ({
  map,
  center,
  radius,
  fillColor,
  strokeColor,
  fillOpacity = 0.5,
  strokeOpacity = 0.8,
  strokeWeight = 2,
  editable = false,
  draggable = false,
  onClick,
  onCenterChanged,
  onRadiusChanged,
  type,
}) => {
  const circleRef = useRef<google.maps.Circle>();

  const colors = getGeofenceColors(type);
  const finalFillColor = fillColor ?? colors.fillColor;
  const finalStrokeColor = strokeColor ?? colors.strokeColor;

  // Create circle once when component mounts and map is available
  useEffect(() => {
    if (!map) {return;}

    const circle = new google.maps.Circle({
      map,
      center,
      radius,
      fillColor: finalFillColor,
      fillOpacity,
      strokeColor: finalStrokeColor,
      strokeOpacity,
      strokeWeight,
      editable,
      draggable,
      clickable: !!onClick,
    });

    circleRef.current = circle;

    if (onClick) {
      circle.addListener('click', onClick);
    }

    if (onCenterChanged && (editable || draggable)) {
      circle.addListener('center_changed', () => {
        const newCenter = circle.getCenter();
        if (newCenter) {
          onCenterChanged({
            lat: newCenter.lat(),
            lng: newCenter.lng(),
          });
        }
      });
    }

    if (onRadiusChanged && editable) {
      circle.addListener('radius_changed', () => {
        const newRadius = circle.getRadius();
        if (newRadius) {
          onRadiusChanged(newRadius);
        }
      });
    }

    // Cleanup only when component unmounts or map changes
    return () => {
      google.maps.event.clearInstanceListeners(circle);
      circle.setMap(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]); // Only recreate when map instance changes, other props handled in separate effect

  // Update circle properties when props change
  useEffect(() => {
    if (circleRef.current) {
      circleRef.current.setCenter(center);
      circleRef.current.setRadius(radius);
      circleRef.current.setOptions({
        fillColor: finalFillColor,
        fillOpacity,
        strokeColor: finalStrokeColor,
        strokeOpacity,
        strokeWeight,
        editable,
        draggable,
      });
    }
  }, [
    center,
    radius,
    finalFillColor,
    fillOpacity,
    finalStrokeColor,
    strokeOpacity,
    strokeWeight,
    editable,
    draggable,
  ]);

  return null;
};

export default GeofenceOverlay;